import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { api } from '@/utils/api';
import { 
  TrendingUp, 
  Wallet, 
  Users, 
  Building2, 
  MapPin,
  ChevronRight,
  Clock,
  AlertTriangle
} from 'lucide-react';
import { formatINR } from '@/utils/currency';
import BrokerSidebar from '@/components/broker/BrokerSidebar';
import EarningsCalculator from '@/components/broker/EarningsCalculator';
import { DashboardSkeleton, ErrorBanner } from '@/components/ui/loading-states';

// Helper function to summarize property units
const getPropertySummary = (property) => {
  const units = property.units || [];
  const openUnits = units.filter(u => u.status === 'OPEN');
  const fundedUnits = units.filter(u => u.status === 'FUNDED');
  
  return {
    totalUnits: units.length,
    openUnits: openUnits.length,
    fundedUnits: fundedUnits.length,
    totalCapitalTarget: units.reduce((sum, u) => sum + (u.financial?.capitalTarget || 0), 0),
    totalCapitalRaised: units.reduce((sum, u) => sum + (u.financial?.capitalRaised || 0), 0),
  };
};

export default function BrokerDashboard({ isTourMode = false }) {
  const navigate = useNavigate();
  const [selectedCity, setSelectedCity] = useState('All Cities');
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statsError, setStatsError] = useState(null);
  const [brokerStats, setBrokerStats] = useState({
    totalRaised: 0,
    lifetimeRaised: 0,
    totalCommission: 0,
    pendingCommission: 0,
    activeInvestors: 0,
    dealsContributed: 0,
    rank: 12,
    tier: null
  });

  const [brokerApproved, setBrokerApproved] = useState(true);

  // Check broker approval status on mount
  useEffect(() => {
    const checkBrokerStatus = async () => {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (!user.id || user.role !== 'broker') {
        navigate('/broker/auth');
        return;
      }
      
      try {
        const response = await api.get(`/broker/status/${user.id}`);
        setBrokerApproved(response.data.brokerStatus === 'APPROVED');
      } catch (error) {
        console.error('Error checking broker status:', error);
      }
    };
    
    if (!isTourMode) {
      checkBrokerStatus();
    }
  }, [navigate, isTourMode]);

  useEffect(() => {
    fetchProperties();
    fetchBrokerStats();
  }, []);

  const fetchBrokerStats = useCallback(async () => {
    setStatsError(null);
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const brokerId = user.brokerId || user.userId || user.id;

      if (brokerId) {
        const res = await api.get(`/metrics/broker/${brokerId}`);
        const d = res.data;
        setBrokerStats({
          totalRaised: d.capitalCommitted || 0,
          lifetimeRaised: d.capitalCommitted || 0,
          totalCommission: d.totalCommission || 0,
          pendingCommission: d.pendingCommission || 0,
          activeInvestors: d.activeInvestors || 0,
          dealsContributed: d.dealsContributed || 0,
          rank: 12,
          tier: d.tier,
        });
      }
    } catch (error) {
      console.error('Error fetching broker stats:', error);
      setStatsError('Failed to load dashboard stats.');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchProperties = async () => {
    try {
      const response = await api.get('/projects/opportunities');
      const projectsData = response.data || [];
      
      const transformed = projectsData.map(p => ({
        projectId: p.projectId,
        name: p.name,
        location: p.location?.sector || '',
        city: p.location?.city || '',
        type: p.type || 'residential',
        units: p.units || [],
      }));
      
      setProperties(transformed);
      if (transformed.length > 0) {
        setSelectedProperty(transformed[0]);
      }
    } catch (error) {
      console.error('Error fetching properties:', error);
      setProperties([]);
    } finally {
      setLoading(false);
    }
  };

  // Get unique cities from properties
  const cities = ['All Cities', ...new Set(properties.map(p => p.city).filter(Boolean))];
  
  // Filter properties by city
  const filteredProperties = selectedCity === 'All Cities' 
    ? properties 
    : properties.filter(p => p.city === selectedCity);

  // Format large numbers for display
  const formatStatValue = (val) => {
    if (val >= 10000000) return `${(val/10000000).toFixed(2)}`;
    if (val >= 100000) return `${(val/100000).toFixed(2)}`;
    return val.toLocaleString('en-IN');
  };

  const getStatSuffix = (val) => {
    if (val >= 10000000) return 'Cr';
    if (val >= 100000) return 'L';
    return '';
  };

  return (
    <div className="min-h-screen bg-black flex">
      {/* Sidebar */}
      <BrokerSidebar />

      {/* Main Content */}
      <main className="flex-1 ml-0 md:ml-64 p-6 md:p-10">
        {/* Header */}
        <div className="mb-10" data-tour="dashboard-header">
          <p className="text-xs text-gray-600 uppercase tracking-widest mb-2">Partner Dashboard</p>
          <h1 className="text-2xl font-light text-white">
            Welcome back, <span className="text-[#4ADE80]">{JSON.parse(localStorage.getItem('user') || '{}').name?.split(' ')[0] || 'Partner'}</span>
          </h1>
        </div>

        {/* Pending Approval Banner */}
        {!brokerApproved && (
          <div className="mb-8 p-4 border border-amber-500/30 bg-amber-500/5 flex items-start gap-3" data-testid="pending-approval-banner">
            <Clock className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-amber-400 font-medium">Awaiting Admin Approval</p>
              <p className="text-xs text-amber-400/70 mt-1">Your account is under review. You can browse properties and update your settings, but referring investors is disabled until your application is approved.</p>
            </div>
          </div>
        )}

        {/* Stats - Horizontal Bar */}
        <div className="mb-10" data-tour="stats-cards">
          {statsError && <ErrorBanner message={statsError} onRetry={fetchBrokerStats} />}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/5 border border-white/10">
            <div className="bg-black px-6 py-6">
              <div className="flex items-center justify-between mb-3">
                <p className="text-[10px] text-gray-600 uppercase tracking-widest">Capital Raised</p>
                <TrendingUp className="w-4 h-4 text-gray-700" />
              </div>
              <p className="font-mono text-3xl font-light text-white">
                {formatStatValue(brokerStats.totalRaised)}
                <span className="text-base text-gray-600 ml-1">{getStatSuffix(brokerStats.totalRaised)}</span>
              </p>
              <p className="text-xs text-gray-700 mt-2">Cumulative business</p>
            </div>
            
            <div className="bg-black px-6 py-6">
              <div className="flex items-center justify-between mb-3">
                <p className="text-[10px] text-gray-600 uppercase tracking-widest">Commission Earned</p>
                <Wallet className="w-4 h-4 text-gray-700" />
              </div>
              <p className="font-mono text-3xl font-light text-[#4ADE80]">
                {formatStatValue(brokerStats.totalCommission)}
                <span className="text-base text-gray-600 ml-1">{getStatSuffix(brokerStats.totalCommission)}</span>
              </p>
              {brokerStats.pendingCommission > 0 && (
                <p className="text-xs text-yellow-500 mt-2">₹{(brokerStats.pendingCommission/100000).toFixed(2)}L pending</p>
              )}
            </div>
            
            <div className="bg-black px-6 py-6">
              <div className="flex items-center justify-between mb-3">
                <p className="text-[10px] text-gray-600 uppercase tracking-widest">Active Investors</p>
                <Users className="w-4 h-4 text-gray-700" />
              </div>
              <p className="font-mono text-3xl font-light text-white">{brokerStats.activeInvestors}</p>
              <p className="text-xs text-gray-700 mt-2">Referred investors</p>
            </div>
            
            <div className="bg-black px-6 py-6">
              <div className="flex items-center justify-between mb-3">
                <p className="text-[10px] text-gray-600 uppercase tracking-widest">Deals Contributed</p>
                <Building2 className="w-4 h-4 text-gray-700" />
              </div>
              <p className="font-mono text-3xl font-light text-white">{brokerStats.dealsContributed}</p>
              <p className="text-xs text-gray-700 mt-2">Properties</p>
            </div>
          </div>
        </div>

        {/* Main Grid - Property Selector & Calculator */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Property Selector */}
          <div className="lg:col-span-4" data-tour="deal-selector">
            <div className="border border-white/10 mb-6">
              {/* Header */}
              <div className="px-6 py-4 border-b border-white/10 bg-white/5">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-medium text-white">Available Properties</h3>
                    <p className="text-xs text-gray-600 mt-1">Select property to view units</p>
                  </div>
                  <span className="px-2 py-1 bg-emerald-500/20 text-emerald-400 text-xs font-mono rounded">
                    {filteredProperties.length} Properties
                  </span>
                </div>
              </div>
              
              {/* City Filter */}
              <div className="px-6 py-4 border-b border-white/10">
                <select
                  value={selectedCity}
                  onChange={(e) => setSelectedCity(e.target.value)}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-[#4ADE80]/50"
                  data-testid="city-dropdown"
                >
                  {cities.map(city => (
                    <option key={city} value={city} className="bg-black">
                      {city}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Property List */}
              <div className="max-h-[400px] overflow-y-auto">
                {loading ? (
                  <div className="px-6 py-8 text-center">
                    <div className="animate-spin w-6 h-6 border-2 border-[#4ADE80] border-t-transparent rounded-full mx-auto mb-2" />
                    <p className="text-gray-500 text-sm">Loading properties...</p>
                  </div>
                ) : filteredProperties.length === 0 ? (
                  <div className="px-6 py-8 text-center">
                    <p className="text-gray-500 text-sm">No properties available</p>
                  </div>
                ) : (
                  filteredProperties.map((property) => {
                    const summary = getPropertySummary(property);
                    const isSelected = selectedProperty?.projectId === property.projectId;
                    
                    return (
                      <button
                        key={property.projectId}
                        onClick={() => setSelectedProperty(property)}
                        className={`w-full px-6 py-5 border-b border-white/5 text-left transition-all hover:bg-white/5 ${
                          isSelected ? 'bg-white/5 border-l-2 border-l-[#4ADE80]' : ''
                        }`}
                        data-testid={`property-item-${property.projectId}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className={`text-sm font-medium mb-1 ${isSelected ? 'text-white' : 'text-gray-400'}`}>
                              {property.name}
                            </p>
                            <div className="flex items-center gap-1 text-xs text-gray-600 mb-2">
                              <MapPin className="w-3 h-3" />
                              {property.location}, {property.city}
                            </div>
                          </div>
                          <ChevronRight className={`w-4 h-4 ${isSelected ? 'text-[#4ADE80]' : 'text-gray-600'}`} />
                        </div>
                        
                        {/* Unit Summary */}
                        <div className="flex items-center gap-3 mt-2">
                          <span className="px-2 py-0.5 bg-emerald-500/10 text-emerald-400 text-[10px] font-mono rounded">
                            {summary.openUnits} Open
                          </span>
                          {summary.fundedUnits > 0 && (
                            <span className="px-2 py-0.5 bg-gray-500/10 text-gray-500 text-[10px] font-mono rounded">
                              {summary.fundedUnits} Funded
                            </span>
                          )}
                        </div>
                        
                        {/* Financial Summary */}
                        <div className="grid grid-cols-2 gap-4 mt-3 text-xs">
                          <div>
                            <span className="text-gray-600">Total Target</span>
                            <p className="font-mono text-white">{formatINR(summary.totalCapitalTarget)}</p>
                          </div>
                          <div>
                            <span className="text-gray-600">Raised</span>
                            <p className="font-mono text-[#4ADE80]">{formatINR(summary.totalCapitalRaised)}</p>
                          </div>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </div>
            
            {/* Hot Deal / Closing Soon */}
            {filteredProperties.length > 0 && (
              <button
                onClick={() => {
                  // Find property with unit closing soon
                  const propertyWithClosingSoon = filteredProperties.find(p => 
                    p.units?.some(u => u.status === 'OPEN' && u.daysLeft <= 30)
                  );
                  if (propertyWithClosingSoon) setSelectedProperty(propertyWithClosingSoon);
                }}
                className="w-full border border-[#4ADE80]/30 bg-[#4ADE80]/5 px-6 py-4 hot-deal-card hover:bg-[#4ADE80]/10 transition-all text-left"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-[#4ADE80] uppercase tracking-wider mb-1">Closing Soon</p>
                    <p className="text-sm text-white">{filteredProperties[0]?.name || 'No properties'}</p>
                  </div>
                  <p className="font-mono text-sm text-[#4ADE80]">30 days</p>
                </div>
              </button>
            )}
          </div>

          {/* Earnings Calculator */}
          <div className="lg:col-span-8">
            <EarningsCalculator 
              property={selectedProperty}
              brokerCumulativeCapital={brokerStats.lifetimeRaised || brokerStats.totalRaised}
              brokerApproved={brokerApproved}
            />
          </div>
        </div>
      </main>
    </div>
  );
}
